/**
 * The deterministic rule checker.
 *
 * `analyze` is the single source of truth for "what is wrong with this
 * position". Everything else in the engine is built on it: the grader counts
 * its findings, the legality filter asks it whether a hypothetical note would
 * introduce a new one, and the solver uses that filter to search for a legal
 * completion.
 *
 * Because it runs thousands of times per keystroke (once per candidate pitch per
 * measure), it is written as plain loops over arrays with no allocation beyond
 * the result.
 *
 * Table of contents
 *   1. tip texts
 *   2. analyze()
 *      a. vertical intervals
 *      b. motion between measures
 *      c. opening / cadence / final formulas
 *      d. melodic checks on the counterpoint line
 */

import type { Analysis, Exercise, Finding, IntervalInfo, Motion, RulesProfile } from './types';
import { measureCount } from './types';
import {
  diatonicDistance,
  edoOf,
  nameOfPitch,
  ordinal,
  tuningStepOf,
  tuningStepsBetween,
} from './pitch';
import { dissonanceName, isConsonant, isPerfectConsonance, isTritone, simpleNumber } from './intervals';
import { rulesFor } from './rules';

/* ---- 1. tip texts ------------------------------------------------------- */

/**
 * The lesson behind each finding code, in plain prose. Rendered as the "why"
 * beneath the findings list; keyed so a finding carries its teaching with it.
 */
export const TIPS: Record<string, string> = {
  P4: 'The **perfect fourth** is the classic trap: it sounds sweet, but between two voices it counts as a dissonance — the ear hears the lower note wanting to resolve.',
  TRI: 'That vertical is a **tritone** (augmented 4th or diminished 5th) — the most restless dissonance there is. Check the interval quality in the active tuning, not just its staff number.',
  DISS: 'Only **3rds, 5ths, 6ths and octaves** are consonant here. Seconds and sevenths always need resolution, so first species bans them outright.',
  UNI: 'A **unison** inside the exercise makes the two voices collapse into one. Save it for the first or last note.',
  PAR: '**Parallel fifths or octaves** glue the voices together — the ear stops hearing two independent lines. Approach a perfect interval by contrary or oblique motion.',
  DIR: '**Direct (hidden) perfects**: both voices rushing the same way into a 5th or octave gives a whiff of the parallel even without one. Let one voice step against the other.',
  RUN: 'More than three **3rds or 6ths in a row** turns counterpoint into a harmonised melody. Break the chain with a 5th, an octave, or a contrary leap.',
  REP: '**Repeated notes** stall the line in first species. Every measure should move.',
  START: '**Opening formula**: begin on a note that makes the key unmistakable from the first sound. Under Gran\'s rules an upper voice may also open at the 3rd or 10th.',
  PP2: 'Gran\'s rule: **two perfect consonances in a row** — even different ones, like a 5th into an octave — glue the voices together. Put an imperfect consonance between them.',
  WIDE: 'Beyond a **10th** the voices stop sounding like companions. Keep them within a 10th of each other.',
  PEAK: 'When both lines **peak in the same measure** they steal each other\'s thunder. Offset the high points so each gets its moment.',
  CAD: '**Cadence formula**: the penultimate bar takes a **6th** when the cantus firmus is below (or a **3rd** when it is above), and then both voices step into the tonic.',
  FINAL: '**Final bar**: end on the tonic — a unison or an octave with the cantus firmus.',
  XV: '**Voice crossing**: your line wandered past the cantus firmus. Keep each voice in its own lane.',
  AUG2: 'An **augmented second** crept in — the melodic-minor pothole near the cadence. Approach the raised leading tone from above, or step through it from the raised 6th.',
  LEAP: 'A big **leap** is fine occasionally, but recover it: step back in the opposite direction so the line regains its balance.',
  IDLE: 'Aim the line at **one climax**, approach it and leave it by step where you can, and let contrary motion do most of the work.',
};

/* ---- 2. analyze() ------------------------------------------------------- */

/**
 * Check an exercise against a rules profile.
 *
 * Rests (`null` in `cp`) are simply skipped: an unfinished line produces no
 * findings for the measures it has not reached, which is what lets the same
 * function drive both grading and the allowed-notes filter.
 */
export function analyze(ex: Exercise, profile: RulesProfile): Analysis {
  const n = measureCount(ex);
  const edo = edoOf(ex);
  const R = rulesFor(profile);
  const findings: Finding[] = [];
  const intervals: (IntervalInfo | null)[] = [];
  const motions: (Motion | null)[] = [];

  const upper = (i: number) => (ex.cfPos === 'lower' ? ex.cp[i]! : ex.cf[i]);
  const lower = (i: number) => (ex.cfPos === 'lower' ? ex.cf[i] : ex.cp[i]!);

  /* -- a. vertical intervals -- */
  for (let i = 0; i < n; i++) {
    if (ex.cp[i] == null) {
      intervals.push(null);
      continue;
    }
    const dd = diatonicDistance(upper(i), lower(i), ex);
    const steps = tuningStepsBetween(upper(i), lower(i), i, ex);
    if (dd < 0 || steps < 0) {
      findings.push(f(i, 'XV', 'error', true, 'cons', `m${i + 1}: voice crossing`));
      intervals.push(null);
      continue;
    }
    const consonant = isConsonant(dd, steps, edo);
    intervals.push({
      dd,
      steps,
      num: simpleNumber(dd),
      cls: dd % 7,
      consonant,
      perfect: isPerfectConsonance(dd, steps, edo),
    });

    if (!consonant) {
      const cls = dd % 7;
      const s = ((steps % edo) + edo) % edo;
      const code = cls === 3 && s === (edo === 31 ? 13 : 5) ? 'P4' : isTritone(s, edo) ? 'TRI' : 'DISS';
      findings.push(
        f(i, code, 'error', true, 'cons',
          `m${i + 1}: ${dissonanceName(dd, steps, edo)} (${nameOfPitch(lower(i), edo)}–${nameOfPitch(upper(i), edo)})`),
      );
    } else if (dd === 0 && i > 0 && i < n - 1) {
      findings.push(f(i, 'UNI', 'error', true, 'cons', `m${i + 1}: unison inside the exercise`));
    } else if (R.maxSpacing != null && dd > R.maxSpacing) {
      findings.push(f(i, 'WIDE', 'warning', true, 'cons', `m${i + 1}: voices spaced beyond a 10th`));
    }
  }

  /* -- b. motion between consecutive complete measures -- */
  let run = { cls: -1, len: 0 };
  for (let i = 1; i < n; i++) {
    const here = intervals[i];
    const prev = intervals[i - 1];
    if (!here || !prev) {
      motions.push(null);
      run = { cls: -1, len: 0 };
      continue;
    }
    const sCF = Math.sign(tuningStepOf(ex.cf[i], i, ex) - tuningStepOf(ex.cf[i - 1], i - 1, ex));
    const sCP = Math.sign(tuningStepOf(ex.cp[i]!, i, ex) - tuningStepOf(ex.cp[i - 1]!, i - 1, ex));
    let m: Motion = sCF === 0 || sCP === 0 ? 'o' : sCF === sCP ? 's' : 'c';

    if (here.perfect && prev.perfect && here.cls === prev.cls) {
      const what = here.cls === 0 ? 'octaves' : 'fifths';
      findings.push(
        f(i, 'PAR', 'error', true, 'perf',
          `m${i}–${i + 1}: consecutive ${what}${m === 'c' ? ' (by contrary motion)' : ''}`),
      );
      m = 'p';
    } else if (R.barTwoPerfects && here.perfect && prev.perfect) {
      findings.push(f(i, 'PP2', 'error', true, 'perf', `m${i}–${i + 1}: two perfect consonances in a row`));
    } else if (m === 's' && here.perfect) {
      findings.push(
        f(i, 'DIR', 'error', true, 'perf',
          `m${i}–${i + 1}: direct motion into a perfect ${here.cls === 0 ? 'octave' : 'fifth'}`),
      );
    }
    motions.push(m);

    if (ex.cp[i] === ex.cp[i - 1]) {
      const sparing = R.repeatedNote === 'sparing';
      findings.push(
        f(i, 'REP', sparing ? 'warning' : 'error', !sparing, 'rep',
          `m${i}–${i + 1}: repeated note${sparing ? ' (fine sparingly)' : ''}`),
      );
    }

    // Runs of the same imperfect consonance: four in a row is one too many.
    const cls = here.consonant && (here.cls === 2 || here.cls === 5) ? here.cls : -1;
    if (cls !== -1 && cls === run.cls) {
      run.len++;
      if (run.len === 4) {
        findings.push(
          f(i, 'RUN', 'error', true, 'run',
            `m${i - 2}–${i + 1}: four ${cls === 2 ? '3rds' : '6ths'} in a row`),
        );
      }
    } else {
      run = { cls, len: 1 };
    }
  }

  /* -- c. opening / cadence / final formulas -- */
  const first = intervals[0];
  if (first) {
    const allowed = ex.cfPos === 'lower' ? R.openingAbove : R.openingBelow;
    const ok = allowed.includes(first.dd) && first.consonant;
    if (!ok) {
      findings.push(
        f(0, 'START', 'error', true, 'form',
          ex.cfPos === 'lower'
            ? profile === 'gran'
              ? 'm1: begin on scale degree 1, 3 or 5'
              : 'm1: begin on the tonic or dominant (a perfect consonance)'
            : 'm1: begin on the tonic'),
      );
    }
  }
  const penult = intervals[n - 2];
  if (penult) {
    const want = ex.cfPos === 'lower' ? 5 : 2; // a 6th above, or a 3rd below
    if (penult.cls !== want || !penult.consonant) {
      findings.push(
        f(n - 2, 'CAD', 'error', true, 'form',
          `m${n - 1}: the penultimate bar wants a ${want === 5 ? '6th' : '3rd'}`),
      );
    }
  }
  const last = intervals[n - 1];
  if (last && last.cls !== 0) {
    findings.push(f(n - 1, 'FINAL', 'error', true, 'form', `m${n}: end on the tonic (unison or octave)`));
  }

  /* -- d. melodic checks on the counterpoint line -- */
  for (let i = 1; i < n; i++) {
    const here = ex.cp[i];
    const prev = ex.cp[i - 1];
    if (here == null || prev == null) continue;
    const dd = Math.abs(diatonicDistance(here, prev, ex));
    const steps = Math.abs(tuningStepOf(here, i, ex) - tuningStepOf(prev, i - 1, ex));
    if (dd === 1 && steps === (edo === 31 ? 7 : 3)) {
      findings.push(f(i, 'AUG2', 'warning', true, 'mel', `m${i}–${i + 1}: augmented 2nd in your line`));
    } else if (isTritone(steps, edo)) {
      findings.push(f(i, 'AUG2', 'warning', true, 'mel', `m${i}–${i + 1}: melodic tritone`));
    } else if (dd === 6 || dd > 7) {
      findings.push(
        f(i, 'LEAP', 'warning', true, 'mel', `m${i}–${i + 1}: leap of a ${ordinal(dd + 1)} — too wide`),
      );
    } else if (dd > 2 && i + 1 < n && ex.cp[i + 1] != null) {
      const recovery = tuningStepOf(ex.cp[i + 1]!, i + 1, ex) - tuningStepOf(here, i, ex);
      const approach = tuningStepOf(here, i, ex) - tuningStepOf(prev, i - 1, ex);
      const recoveryDiatonic = Math.abs(diatonicDistance(ex.cp[i + 1]!, here, ex));
      if (Math.sign(recovery) === Math.sign(approach) && recoveryDiatonic > 1) {
        findings.push(f(i, 'LEAP', 'warning', false, 'mel', `m${i + 1}: leap not recovered by a step back`));
      }
    }
  }

  // Shape checks only make sense once the line is complete.
  const placed = ex.cp.filter((d): d is number => d != null);
  let climaxOk = true;
  if (placed.length === n) {
    const hi = Math.max(...placed);
    climaxOk = placed.filter((d) => d === hi).length === 1;
    if (!climaxOk) {
      findings.push(
        f(ex.cp.indexOf(hi), 'IDLE', 'warning', false, 'mel',
          'the line\'s high point recurs — aim for a single climax'),
      );
    } else if (R.offsetClimax && ex.cp.indexOf(hi) === ex.cf.indexOf(Math.max(...ex.cf))) {
      findings.push(
        f(ex.cp.indexOf(hi), 'PEAK', 'warning', false, 'mel',
          `both lines peak in m${ex.cp.indexOf(hi) + 1} — offset the high points`),
      );
    }
  }

  return { intervals, motions, findings, climaxOk };
}

/** Terse `Finding` constructor — this module builds a lot of them. */
function f(
  measure: number,
  code: Finding['code'],
  severity: Finding['severity'],
  hard: boolean,
  dimension: Finding['dimension'],
  message: string,
): Finding {
  return { measure, code, severity, hard, dimension, message };
}

/** True for findings that disqualify a candidate note in the legality filter. */
export const isHard = (finding: Finding): boolean => finding.hard;

/** Identity of a finding, so two analyses can be diffed. */
export const findingKey = (finding: Finding): string =>
  `${finding.measure}|${finding.code}|${finding.message}`;
